// UppyManager — Module-level singleton that manages Uppy instances
// eslint-disable-next-line import/no-extraneous-dependencies
import Uppy from '@uppy/core';

import getUploaderProgressState from './useGetUploaderProgressState';

interface ProgressSnapshot {
    pending: number;
    paused: number;
    uploading: number;
    total: number;
    uploaded: number;
    cancelled: number;
    processing: number;
    failed: number;
    bytesTotal: number;
    bytesUploaded: number;
}

type UppyInstance = Uppy<Record<string, unknown>, Record<string, unknown>>;
type Listener = () => void;
type UploadPredicate = (
    state: ReturnType<typeof getUploaderProgressState>
) => boolean;

class UppyManager {
    private instances = new Map<string, UppyInstance>();

    private instanceListeners = new Set<Listener>();

    private progressListeners = new Set<Listener>();

    private progressInterval: ReturnType<typeof setInterval> | null = null;

    private idsSnapshot: string[] = [];

    private instanceVersion = 0;

    private progressSnapshot: ProgressSnapshot | null = null;

    // Register an Uppy instance. If an instance with this ID already exists, it is replaced
    add(id: string, uppy: UppyInstance) {
        const existing = this.instances.get(id);
        this.instances.set(id, uppy);

        if (existing !== uppy) {
            this.updateIdsSnapshot();
            this.notifyInstanceListeners();
            this.manageProgressInterval();
        }
    }

    // Remove an Uppy instance by ID.
    remove(id: string) {
        if (this.instances.has(id)) {
            this.instances.delete(id);
            this.updateIdsSnapshot();
            this.notifyInstanceListeners();
            this.manageProgressInterval();
        }
    }

    // Remove all instances.
    clear() {
        if (this.instances.size > 0) {
            this.instances.clear();
            this.updateIdsSnapshot();
            this.notifyInstanceListeners();
            this.manageProgressInterval();
        }
    }

    get(id: string): UppyInstance | null {
        return this.instances.get(id) ?? null;
    }

    has(id: string): boolean {
        return this.instances.has(id);
    }

    getAll(): Record<string, UppyInstance> {
        return Object.fromEntries(this.instances);
    }

    // Get the number of instances.
    get size(): number {
        return this.instances.size;
    }

    // Subscribe to instance add/remove events.
    subscribeToInstances = (listener: Listener): (() => void) => {
        this.instanceListeners.add(listener);
        return () => this.instanceListeners.delete(listener);
    };

    // Returns a stable snapshot of instance IDs. Only changes reference when instances are added or removed.
    getInstancesSnapshot = (): string[] => this.idsSnapshot;

    // Subscribe to throttled progress updates.
    subscribeToProgress = (listener: Listener): (() => void) => {
        this.progressListeners.add(listener);
        this.manageProgressInterval();
        return () => {
            this.progressListeners.delete(listener);
            this.manageProgressInterval();
        };
    };

    // Returns the latest progress snapshot (throttled).
    getProgressSnapshot = (): ProgressSnapshot => {
        if (!this.progressSnapshot) {
            this.progressSnapshot = this.computeProgressSnapshot();
        }
        return this.progressSnapshot;
    };

    // Force a progress snapshot recalculation and notify subscribers.
    private tickProgress = () => {
        const prev = this.progressSnapshot;
        const next = this.computeProgressSnapshot();

        // Only notify if something actually changed
        if (
            !prev ||
            prev.bytesUploaded !== next.bytesUploaded ||
            prev.bytesTotal !== next.bytesTotal ||
            prev.uploading !== next.uploading ||
            prev.total !== next.total ||
            prev.uploaded !== next.uploaded ||
            prev.failed !== next.failed
        ) {
            this.progressSnapshot = next;
            this.notifyProgressListeners();
        }
    };

    getCompletedUploads(): UppyInstance[] {
        return this.filterUploads(
            (state) =>
                state.total > 0 &&
                (state.uploaded === state.total ||
                    state.uploaded + state.failed === state.total)
        );
    }

    getCancelledUploads(): UppyInstance[] {
        return this.filterUploads(
            (state) => state.total > 0 && state.cancelled === state.total
        );
    }

    getUploadsWithFailed(): UppyInstance[] {
        return this.filterUploads((state) => state.failed > 0);
    }

    getUploadsWithPaused(): UppyInstance[] {
        return this.filterUploads((state) => state.paused > 0);
    }

    getUploadsWithPending(): UppyInstance[] {
        return this.filterUploads((state) => state.pending > 0);
    }

    getUploadsWithProcessing(): UppyInstance[] {
        return this.filterUploads((state) => state.processing > 0);
    }

    getUploadsWithUploading(): UppyInstance[] {
        return this.filterUploads((state) => state.uploading > 0);
    }

    getUploadProgress(
        id: string
    ): ReturnType<typeof getUploaderProgressState> | null {
        const uppy = this.instances.get(id);
        if (!uppy) return null;
        return getUploaderProgressState(uppy);
    }

    private updateIdsSnapshot() {
        this.instanceVersion += 1;
        this.idsSnapshot = Array.from(this.instances.keys());
    }

    private notifyInstanceListeners() {
        this.instanceListeners.forEach((listener) => {
            try {
                listener();
            } catch (e) {
                // Swallow errors in listeners
            }
        });
    }

    private notifyProgressListeners() {
        this.progressListeners.forEach((listener) => {
            try {
                listener();
            } catch (e) {
                // Swallow errors in listeners
            }
        });
    }

    private manageProgressInterval() {
        const hasSubscribers = this.progressListeners.size > 0;
        const hasInstances = this.instances.size > 0;

        if (hasSubscribers && hasInstances && !this.progressInterval) {
            // Poll every 1 second for progress updates
            this.progressInterval = setInterval(
                () => this.tickProgress(),
                1000
            );
        } else if (
            (!hasSubscribers || !hasInstances) &&
            this.progressInterval
        ) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
    }

    // Compute aggregate progress across all Uppy instances.
    private computeProgressSnapshot(): ProgressSnapshot {
        const snapshot: ProgressSnapshot = {
            pending: 0,
            paused: 0,
            uploading: 0,
            total: 0,
            uploaded: 0,
            cancelled: 0,
            processing: 0,
            failed: 0,
            bytesTotal: 0,
            bytesUploaded: 0,
        };

        this.instances.forEach((uppy) => {
            const state = getUploaderProgressState(uppy);
            snapshot.pending += state.pending;
            snapshot.paused += state.paused;
            snapshot.uploading += state.uploading;
            snapshot.total += state.total;
            snapshot.uploaded += state.uploaded;
            snapshot.cancelled += state.cancelled;
            snapshot.processing += state.processing;
            snapshot.failed += state.failed;
            snapshot.bytesTotal += state.progress.bytesTotal;
            snapshot.bytesUploaded += state.progress.bytesUploaded;
        });

        return snapshot;
    }

    // Filter uploads by a predicate on their progress state.
    private filterUploads(predicate: UploadPredicate): UppyInstance[] {
        const result: UppyInstance[] = [];
        this.instances.forEach((uppy) => {
            const state = getUploaderProgressState(uppy);
            if (predicate(state)) {
                result.push(uppy);
            }
        });
        return result;
    }
}

// Singleton instance
const uppyManager = new UppyManager();

export default uppyManager;
